import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import StatCounter from "../components/StatCounter";
import SectionHeading from "../components/SectionHeading";
import PressStrip from "../components/PressStrip";
import { IMAGES } from "../lib/images";
import { api } from "../lib/api";
import { fileUrl } from "../lib/storage";

const ease = [0.16, 1, 0.3, 1];

const projectImage = (p) => {
  if (p.hero_image_url) return fileUrl(p.hero_image_url);
  if (p.hero_image && IMAGES[p.hero_image]) return IMAGES[p.hero_image];
  return IMAGES.elliott_twin_towers;
};

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/projects").then((r) => {
      const list = (r.data || []).filter((p) => p.featured !== false).slice(0, 3);
      setFeatured(list);
    }).catch(() => {});
  }, []);
  return (
    <Layout>
      <SEO title={null} path="/" />
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative h-screen min-h-[720px] w-full overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={IMAGES.tampa_skyline}
            alt="Tampa skyline at night"
            className="w-full h-full object-cover scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/15 via-transparent to-background/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/50 via-background/5 to-transparent" />
        </div>

        <div className="relative h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-24 z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease }}
            className="overline mb-6"
          >
            Elliott International Development · Tampa, Florida
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.55, ease }}
            className="font-serif text-5xl sm:text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.95] font-light tracking-[-0.02em] max-w-5xl text-balance"
          >
            Redefining<br />
            <em className="text-primary italic font-light">the skyline</em><br />
            of Tampa.
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease }}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/elliott-towers"
              data-testid="hero-cta-elliott-towers"
              className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-all"
            >
              Explore Elliott Towers
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/investor-relations"
              data-testid="hero-cta-investor"
              className="group inline-flex items-center gap-3 border border-primary/40 text-primary px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary hover:text-primary-foreground transition-all"
            >
              Investor Relations
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center text-foreground/80 px-2 py-5 text-[13px] uppercase tracking-[0.22em] hover:text-primary transition-colors"
            >
              Contact —
            </Link>
          </motion.div>
        </div>

        <div className="absolute bottom-8 right-6 lg:right-12 z-10 flex flex-col items-end gap-2 text-foreground/50">
          <div className="overline">Scroll</div>
          <ArrowDown className="w-4 h-4 animate-bounce" />
        </div>
        <div className="absolute bottom-8 left-6 lg:left-12 z-10 font-mono text-xs text-foreground/40">
          27.9506° N / 82.4572° W
        </div>
      </section>

      {/* INTRO STATEMENT */}
      <section className="py-32 lg:py-44 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline">— EID 01</div>
            <div className="overline mt-2 text-foreground/50">A new institutional developer</div>
          </div>
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.2, ease }}
              className="font-serif text-3xl md:text-5xl leading-[1.15] font-light tracking-tight text-balance"
            >
              Elliott International Development is a Tampa-based developer
              of <em className="text-primary italic">iconic mixed-use towers</em>, ultra-luxury
              residences, hospitality, and transformational urban environments —
              designed to define a generation of American skylines.
            </motion.p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-primary/10 py-20 lg:py-28 px-6 lg:px-12 bg-card">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-16">
          <StatCounter value={1} prefix="$" suffix=".1B" label="Pipeline Value" />
          <StatCounter value={1} suffix=".3M+ SF" label="Development Pipeline" />
          <StatCounter value={740} suffix=" ft" label="Elliott Towers Height" />
          <div className="flex flex-col gap-3" data-testid="stat-positioning">
            <div className="font-serif text-3xl md:text-5xl tracking-tight text-primary italic">Tampa's Tallest</div>
            <div className="overline text-foreground/60">Positioning</div>
          </div>
        </div>
      </section>

      <PressStrip />

      {/* FEATURED DEVELOPMENTS — Elliott Towers spotlight card */}
      <section className="py-32 lg:py-44 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto">
          <SectionHeading
            overline="— Featured Developments"
            title="Elliott Towers"
            subtitle="A landmark twin-tower mixed-use development engineered to become Tampa's tallest building — and the standard for ultra-luxury vertical urbanism in the American South."
          />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 1, ease }}
            className="mt-20"
          >
            <Link to="/elliott-towers" data-testid="featured-card-elliott-towers" className="group block relative overflow-hidden">
              <div className="relative aspect-[16/9] lg:aspect-[21/9] overflow-hidden bg-card">
                <img
                  src={IMAGES.elliott_twin_towers}
                  alt="Elliott Towers"
                  className="w-full h-full object-cover img-grayscale-hover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
                <div className="absolute top-6 left-6 glass px-4 py-2">
                  <div className="font-mono text-xs text-primary">DOWNTOWN · TAMPA</div>
                </div>
                <div className="absolute top-6 right-6 glass px-4 py-2">
                  <div className="font-mono text-xs text-primary uppercase tracking-[0.2em]">Tampa's Tallest Tower</div>
                </div>
              </div>
              <div className="glass-strong px-8 lg:px-12 py-10 grid grid-cols-2 md:grid-cols-5 gap-8">
                <div className="md:col-span-1">
                  <div className="overline text-foreground/50">Project</div>
                  <div className="mt-2 font-serif text-2xl">Elliott Towers</div>
                  <div className="mt-1 text-sm text-muted-foreground">Tampa, Florida</div>
                </div>
                <div><div className="overline text-foreground/50">Height</div><div className="mt-2 font-mono text-2xl text-primary">740 ft</div></div>
                <div><div className="overline text-foreground/50">Stories</div><div className="mt-2 font-mono text-2xl text-primary">62</div></div>
                <div><div className="overline text-foreground/50">Target Completion</div><div className="mt-2 font-mono text-2xl text-primary">2032</div></div>
                <div className="flex items-end justify-end">
                  <ArrowUpRight className="w-7 h-7 text-primary transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ELLIOTT TOWERS SPOTLIGHT */}
      <section className="relative py-32 lg:py-44 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative z-10 order-2 lg:order-1">
            <div className="overline mb-6">— The flagship</div>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.0] font-light tracking-tight">
              Elliott<br /><em className="text-primary italic">Towers</em>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
              A landmark twin-tower mixed-use development planned for the heart of
              Downtown Tampa. Branded residences. A five-star hotel. Class AAA
              office. A public observation deck. One transformational vision.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <div>
                <div className="font-mono text-2xl md:text-3xl text-primary">740<span className="text-base">ft</span></div>
                <div className="overline mt-1 text-foreground/50">Height</div>
              </div>
              <div>
                <div className="font-mono text-2xl md:text-3xl text-primary">62<span className="text-base"></span></div>
                <div className="overline mt-1 text-foreground/50">Stories</div>
              </div>
              <div>
                <div className="font-mono text-2xl md:text-3xl text-primary">2032</div>
                <div className="overline mt-1 text-foreground/50">Target</div>
              </div>
            </div>

            <Link
              to="/elliott-towers"
              data-testid="spotlight-cta-towers"
              className="mt-10 group inline-flex items-center gap-3 text-primary text-[13px] uppercase tracking-[0.22em] border-b border-primary/40 pb-2 hover:border-primary transition-colors"
            >
              Discover Elliott Towers
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.4, ease }}
              className="relative aspect-[4/5] overflow-hidden"
            >
              <img
                src={IMAGES.elliott_twin_towers}
                alt="Elliott Towers render"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 left-6 glass px-4 py-2">
                <div className="font-mono text-xs text-primary">DOWNTOWN · TAMPA</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LEADERSHIP TEASER */}
      <section className="py-32 lg:py-44 px-6 lg:px-12 bg-card border-y border-primary/10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={IMAGES.founder_ceo_portrait}
                alt="Colton Elliott, Founder & CEO"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 25%" }}
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-6">— Founder & CEO</div>
            <blockquote className="font-serif text-2xl md:text-4xl leading-[1.2] font-light tracking-tight text-balance">
              "We are not building structures. We are <em className="text-primary italic">authoring landmarks</em>
              that will define how a city is experienced for the next century."
            </blockquote>
            <div className="mt-10 flex items-center gap-4">
              <div className="gold-divider w-16" />
              <div>
                <div className="font-serif text-lg">Colton Elliott</div>
                <div className="overline text-foreground/50">Founder & CEO</div>
              </div>
            </div>
            <Link
              to="/about"
              data-testid="leadership-cta-about"
              className="mt-10 inline-flex items-center gap-3 text-primary text-[13px] uppercase tracking-[0.22em] border-b border-primary/40 pb-2 hover:border-primary transition-colors"
            >
              About Elliott International <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* INVESTOR OPPORTUNITY */}
      <section className="py-32 lg:py-44 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="overline mb-6">— Capital partnerships</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] font-light tracking-tight text-balance">
              For institutional capital partners.
            </h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed max-w-xl">
              Elliott International Development partners with select family offices,
              institutional funds and strategic capital to deliver generational
              real estate assets across Florida's Gulf Coast and beyond.
            </p>
            <Link
              to="/investor-relations"
              data-testid="investor-cta"
              className="mt-10 inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-colors"
            >
              Investor Relations <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: "Institutional Funds", n: "01" },
              { label: "Family Offices", n: "02" },
              { label: "Strategic Capital", n: "03" },
              { label: "Public–Private", n: "04" },
            ].map((c) => (
              <div key={c.n} className="glass p-8 aspect-square flex flex-col justify-between">
                <div className="font-mono text-primary">{c.n}</div>
                <div className="font-serif text-2xl">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
